import { DateRange } from "react-day-picker";

import { useQuery } from "@tanstack/react-query";

import { supabase } from "~/integrations/supabase/client";

interface PaymentMethodDistribution {
  method: string;
  count: number;
  total_amount: number;
}

export const usePaymentMethodDistribution = (dateRange?: DateRange) => {
  const { data, isLoading } = useQuery({
    queryKey: [
      "payment-method-distribution",
      dateRange?.from?.toISOString(),
      dateRange?.to?.toISOString(),
    ],
    queryFn: async () => {
      // Prepare date parameters
      let dateFrom: string | null = null;
      let dateTo: string | null = null;

      if (dateRange?.from) {
        const year = dateRange.from.getFullYear();
        const month = String(dateRange.from.getMonth() + 1).padStart(2, "0");
        const day = String(dateRange.from.getDate()).padStart(2, "0");
        dateFrom = `${year}-${month}-${day}`;
      }

      if (dateRange?.to) {
        const year = dateRange.to.getFullYear();
        const month = String(dateRange.to.getMonth() + 1).padStart(2, "0");
        const day = String(dateRange.to.getDate()).padStart(2, "0");
        dateTo = `${year}-${month}-${day}`;
      }

      // Build the query
      let query = supabase
        .from("payments")
        .select("payment_method, amount")
        .not("payment_method", "is", null)
        .not("verified_at", "is", null);

      // Apply date filters if provided
      if (dateFrom) {
        query = query.gte("verified_at", `${dateFrom} 00:00:00`);
      }
      if (dateTo) {
        query = query.lte("verified_at", `${dateTo} 23:59:59`);
      }

      const { data: payments, error } = await query;

      if (error) {
        console.error("Error fetching payment method distribution:", error);
        throw new Error("Failed to fetch payment method distribution");
      }

      // Group by payment method and calculate totals
      const distributionMap = new Map<
        string,
        { count: number; total_amount: number }
      >();

      payments?.forEach((payment) => {
        const method = payment.payment_method || "other";
        const existing = distributionMap.get(method) || {
          count: 0,
          total_amount: 0,
        };

        distributionMap.set(method, {
          count: existing.count + 1,
          total_amount: existing.total_amount + Number(payment.amount || 0),
        });
      });

      // Convert to array and sort by count descending
      const distribution: PaymentMethodDistribution[] = Array.from(
        distributionMap.entries()
      )
        .map(([method, data]) => ({
          method,
          count: data.count,
          total_amount: data.total_amount,
        }))
        .sort((a, b) => b.count - a.count);

      return distribution;
    },
  });

  return {
    distribution: data || [],
    isLoading,
  };
};
